import React, { useState } from 'react';
import { PageLayout } from '../layout/PageLayout';
import { CategoryFilter } from './CategoryFilter';
import { QuestionList } from './QuestionList';
import { AskQuestionButton } from './AskQuestionButton';
import { AskQuestionModal } from './AskQuestionModal';
import { QuestionCategory } from '../../types/qa';
import { mockQuestions } from '../../utils/mockQuestions';
import { useAnalytics } from '../../hooks/useAnalytics';

export function QAPage() {
  const [selectedCategory, setSelectedCategory] = useState<QuestionCategory | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [questions, setQuestions] = useState(mockQuestions);
  const { trackQuestionVote, trackCategoryFilter, trackQuestionView } = useAnalytics();

  const handleVote = (id: string) => {
    setQuestions(prev => prev.map(q => 
      q.id === id ? { ...q, votes: q.votes + 1 } : q
    ));
    trackQuestionVote(id);
  };

  const handleCategoryChange = (category: QuestionCategory | 'all') => {
    setSelectedCategory(category);
    trackCategoryFilter(category);
  };

  const handleQuestionView = (questionId: string, category: string) => {
    trackQuestionView(questionId, category);
  };

  const handleAskQuestion = () => {
    setIsModalOpen(true);
  };

  const handleSubmitQuestion = (questionData: { 
    title: string; 
    content: string; 
    category: QuestionCategory; 
    isAnonymous: boolean 
  }) => {
    const newQuestion = {
      id: Math.random().toString(36).substr(2, 9),
      ...questionData,
      authorId: 'currentUser',
      status: 'open',
      votes: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setQuestions(prev => [newQuestion, ...prev]);
  };

  const filteredQuestions = selectedCategory === 'all'
    ? questions
    : questions.filter(q => q.category === selectedCategory);

  return (
    <PageLayout>
      <main className="max-w-4xl mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Q&A Community</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Get answers from medical professionals and community members while maintaining your privacy.
            </p>
          </div>
          <div className="flex justify-between md:justify-end items-center gap-4 w-full md:w-auto">
            <AskQuestionButton onClick={handleAskQuestion} />
          </div>
        </div>

        <div className="mb-8">
          <CategoryFilter
            selectedCategory={selectedCategory}
            onSelect={handleCategoryChange}
          />
        </div>

        <QuestionList
          questions={filteredQuestions}
          onVote={handleVote}
          onQuestionView={handleQuestionView}
        />

        <AskQuestionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmitQuestion}
        />
      </main>
    </PageLayout>
  );
}